(() => {
  'use strict';

  /*
   * Editorial plotting coordinates only. These synthetic values are used solely to render an
   * illustrative shape representing independently documented collectible-whisky market phases.
   * They are not market observations, prices, returns, valuations or proprietary index values.
   */
  const FALLBACK_DATA = [
    ['2013-01',18],['2013-07',20],['2014-01',23],['2014-07',31],['2015-01',37],['2015-07',39],
    ['2016-01',44],['2016-07',47],['2017-01',53],['2017-07',58],['2018-01',72],['2018-07',82],
    ['2019-01',78],['2019-07',84],['2020-01',76],['2020-07',90],['2021-01',96],['2021-04',113],
    ['2021-07',124],['2021-10',132],['2022-01',140],['2022-03',165],['2022-05',158],
    ['2022-07',162],['2022-10',150],['2023-01',132],['2023-04',135],['2023-07',122],
    ['2023-10',126],['2024-01',108],['2024-04',112],['2024-07',106],['2024-10',115],
    ['2025-01',101],['2025-04',105],['2025-07',94],['2025-10',98],['2026-01',84],
    ['2026-04',88],['2026-07',86]
  ];

  const NS = 'http://www.w3.org/2000/svg';
  const XHTML = 'http://www.w3.org/1999/xhtml';
  const BOX = { left: 40, right: 840, top: 60, bottom: 305 };
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const FALLBACK_CONTEXT = {
    2013:['Foundation phase','Measured','Positive'], 2014:['Foundation phase','Measured','Positive'],
    2015:['Early expansion','Building','Positive'], 2016:['Expanding','Building','Positive'],
    2017:['Expanding','Strengthening','Positive'], 2018:['Strong expansion','Elevated','Positive'],
    2019:['Established demand','Steady','Mixed'], 2020:['Resilient demand','Steady','Positive'],
    2021:['Accelerating','Strong','Positive'], 2022:['Peak era','Exceptional','Mixed'],
    2023:['Correction','Cooling','Negative'], 2024:['Recalibration','Selective','Mixed'],
    2025:['Recalibrating','Selective','Mixed'], 2026:['Stabilising','Measured','Stable']
  };
  const FALLBACK_ANNOTATIONS = [
    { period: '2022-03', title: 'PEAK ERA', subtitle: '2021–2022' },
    { period: '2024-07', title: 'MARKET RECALIBRATION', subtitle: '2023–2024' }
  ];
  const FALLBACK_MILESTONES = [
    { period: '2018', headline: '+~40%', description: 'Rare Whisky 100 growth in 2018' },
    { period: '2021–2022', headline: 'Peak Era', description: 'Exceptional collector demand' },
    { period: '2023', headline: '-~9%', description: 'Luxury Whisky Index decline' },
    { period: '2024', headline: '-9%', description: 'Continued market correction' },
    { period: '2025–2026', headline: 'Recalibrating', description: 'A more selective market' }
  ];
  const FALLBACK_DOCUMENT = {
    points: FALLBACK_DATA.map(([period, level]) => {
      const [phase, momentum, direction] = FALLBACK_CONTEXT[Number(period.slice(0, 4))];
      return { period, level, date: new Date(`${period}-01T00:00:00Z`), context: { phase, momentum, direction } };
    }),
    annotations: FALLBACK_ANNOTATIONS,
    milestones: FALLBACK_MILESTONES
  };

  function safeText(value, maximumLength = 160) {
    return typeof value === 'string' && value.trim().length > 0 && value.length <= maximumLength && !/[\u0000-\u001f\u007f]/.test(value);
  }

  function periodDate(period) {
    const match = typeof period === 'string' && /^(\d{4})-(\d{2})$/.exec(period);
    if (!match) return null;
    const month = Number(match[2]);
    if (month < 1 || month > 12) return null;
    return new Date(Date.UTC(Number(match[1]), month - 1, 1));
  }

  function validateContext(context) {
    if (context === undefined || context === null) return {};
    if (typeof context !== 'object' || Array.isArray(context)) return null;
    const clean = {};
    for (const key of ['phase', 'momentum', 'direction']) {
      if (context[key] != null) {
        if (!safeText(context[key])) return null;
        clean[key] = context[key].trim();
      }
    }
    return clean;
  }

  function selectMilestones(milestones) {
    if (milestones == null || (Array.isArray(milestones) && milestones.length === 0)) return FALLBACK_MILESTONES;
    if (!Array.isArray(milestones) || milestones.length > 10) return FALLBACK_MILESTONES;
    const clean = [];
    for (const milestone of milestones) {
      if (!milestone || typeof milestone !== 'object' || Array.isArray(milestone)) return FALLBACK_MILESTONES;
      const fields = [['period', 40], ['headline', 80], ['description', 200]];
      if (fields.some(([key, max]) => !safeText(milestone[key], max) || milestone[key] !== milestone[key].trim() || /[<>]/.test(milestone[key]))) return FALLBACK_MILESTONES;
      clean.push({ period: milestone.period, headline: milestone.headline, description: milestone.description });
    }
    return clean;
  }

  function validatePublicDocument(payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    if (payload.schemaVersion !== 1 || payload.contentType !== 'synthetic-editorial-geometry') return null;
    if (!Number.isInteger(payload.revision) || payload.revision < 1 || !safeText(payload.publishedAt) || !Number.isFinite(Date.parse(payload.publishedAt))) return null;
    if (!Array.isArray(payload.points) || payload.points.length < 2) return null;
    const periods = new Set();
    const points = [];
    for (const point of payload.points) {
      if (!point || typeof point !== 'object' || Array.isArray(point)) return null;
      const date = periodDate(point.period);
      const context = validateContext(point.context);
      if (!date || periods.has(point.period) || !Number.isFinite(point.editorialLevel) || context === null) return null;
      periods.add(point.period);
      points.push({ period: point.period, level: point.editorialLevel, date, context });
    }
    points.sort((left, right) => left.date - right.date);
    const annotations = payload.annotations === undefined ? [] : payload.annotations;
    if (!Array.isArray(annotations)) return null;
    const cleanAnnotations = [];
    for (const annotation of annotations) {
      if (!annotation || typeof annotation !== 'object' || Array.isArray(annotation) || !periods.has(annotation.period) || !safeText(annotation.title)) return null;
      if (annotation.subtitle !== undefined && annotation.subtitle !== null && !safeText(annotation.subtitle)) return null;
      cleanAnnotations.push({ period: annotation.period, title: annotation.title.trim(), subtitle: annotation.subtitle?.trim() || '' });
    }
    return { points, annotations: cleanAnnotations, milestones: selectMilestones(payload.milestones) };
  }

  function createMarketState(fallback = FALLBACK_DOCUMENT) {
    return { active: fallback, source: 'fallback' };
  }

  async function loadPublished(fetchImpl, state, onPublished = () => {}) {
    try {
      const response = await fetchImpl('/apps/archive/market-perspective', { method: 'GET', headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const document = validatePublicDocument(await response.json());
      if (!document) return false;
      state.active = document;
      state.source = 'published';
      onPublished(document);
      return true;
    } catch (_) {
      return false;
    }
  }

  function svgEl(name, attrs = {}, text = '') {
    const node = document.createElementNS(NS, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    if (text) node.textContent = text;
    return node;
  }

  function addTextLines(parent, x, y, lines, attributes = {}) {
    const text = svgEl('text', { x, y, ...attributes });
    lines.forEach((line, index) => text.append(svgEl('tspan', { x, dy: index ? 15 : 0 }, line)));
    parent.append(text);
  }

  function renderMilestones(container, milestones, ownerDocument = document) {
    const items = milestones.map((milestone) => {
      const item = ownerDocument.createElement('li');
      const period = ownerDocument.createElement('span');
      const headline = ownerDocument.createElement('strong');
      const description = ownerDocument.createElement('p');
      period.textContent = milestone.period;
      headline.textContent = milestone.headline;
      description.textContent = milestone.description;
      item.append(period, headline, description);
      return item;
    });
    container.replaceChildren(...items);
  }

  function rangeStart(key, latest, points) {
    if (key === 'full') return points[0].date;
    if (key === 'ytd') return new Date(Date.UTC(latest.getUTCFullYear(), 0, 1));
    const years = Number.parseInt(key, 10);
    return new Date(Date.UTC(latest.getUTCFullYear() - years, latest.getUTCMonth(), 1));
  }

  function axisDates(start, latest, key) {
    const dates = [];
    if (key === 'ytd' || key === '1y') {
      let cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
      while (cursor <= latest) {
        if ([0, 3, 6, 9].includes(cursor.getUTCMonth())) dates.push(new Date(cursor));
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      }
    } else {
      for (let year = start.getUTCFullYear(); year <= latest.getUTCFullYear(); year += 1) {
        const date = new Date(Date.UTC(year, 0, 1));
        if (date >= start && date <= latest) dates.push(date);
      }
    }
    if ((key === 'ytd' || key === '1y') && !dates.some((date) => date.getTime() === latest.getTime())) dates.push(latest);
    return dates;
  }

  function describePoint(point) {
    const year = point.date.getUTCFullYear();
    const details = [['Market phase', point.context.phase], ['Market momentum', point.context.momentum], ['Market direction', point.context.direction]]
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}: ${value}.`)
      .join(' ');
    return `${MONTHS[point.date.getUTCMonth()]} ${year}.${details ? ` ${details}` : ''}`;
  }

  function popover(point, x, y) {
    const below = y < 155;
    const width = 220;
    const height = 126;
    const offsetX = x < 155 ? 8 : x > 730 ? -width - 8 : -width / 2;
    const offsetY = below ? 20 : -height - 18;
    const foreign = svgEl('foreignObject', {
      x: offsetX, y: offsetY, width, height,
      class: `cwm-market__popover${below ? ' cwm-market__popover--below' : ''}`
    });
    const card = document.createElementNS(XHTML, 'div');
    card.setAttribute('class', 'cwm-market__popover-card');
    const heading = document.createElementNS(XHTML, 'strong');
    heading.textContent = `${MONTHS[point.date.getUTCMonth()]} ${point.date.getUTCFullYear()}`;
    const list = document.createElementNS(XHTML, 'dl');
    const details = [['Market Phase', point.context.phase], ['Market Momentum', point.context.momentum], ['Market Direction', point.context.direction]]
      .filter(([, value]) => value);
    details.forEach(([label, value]) => {
      const row = document.createElementNS(XHTML, 'div');
      const term = document.createElementNS(XHTML, 'dt');
      const detail = document.createElementNS(XHTML, 'dd');
      term.textContent = label; detail.textContent = value;
      row.append(term, detail); list.append(row);
    });
    card.append(heading, list); foreign.append(card);
    return foreign;
  }

  function init(section, state) {
    const svg = section.querySelector('.cwm-market__chart');
    const root = svg && svg.querySelector('[data-cwm-chart-root]');
    const controls = Array.from(section.querySelectorAll('[data-cwm-range]'));
    const milestoneContainer = section.querySelector('[data-cwm-milestones]');
    if (!svg || !root || !controls.length || !milestoneContainer) return;

    const gradient = svg.querySelector('linearGradient');
    let currentKey = 'full';

    function render(key) {
      currentKey = key;
      const document = state.active;
      const latest = document.points[document.points.length - 1];
      const start = rangeStart(key, latest.date, document.points);
      const visible = document.points.filter((point) => point.date >= start && point.date <= latest.date);
      const duration = Math.max(1, latest.date - start);
      const low = Math.min(...visible.map((point) => point.level));
      const high = Math.max(...visible.map((point) => point.level));
      const rawSpan = high - low;
      const span = Math.max(60, rawSpan * 1.24);
      const middle = (high + low) / 2;
      const domainLow = middle - span / 2;
      const domainHigh = middle + span / 2;
      const x = (date) => BOX.left + ((date - start) / duration) * (BOX.right - BOX.left);
      const y = (level) => BOX.bottom - ((level - domainLow) / (domainHigh - domainLow)) * (BOX.bottom - BOX.top);
      const coords = visible.map((point) => `${x(point.date).toFixed(2)},${y(point.level).toFixed(2)}`).join(' ');
      const rendered = visible.map((point) => ({ x: x(point.date), y: y(point.level) }));
      const graphYAt = (sampleX) => {
        if (sampleX <= rendered[0].x) return rendered[0].y;
        if (sampleX >= rendered[rendered.length - 1].x) return rendered[rendered.length - 1].y;
        const rightIndex = rendered.findIndex((point) => point.x >= sampleX);
        const leftPoint = rendered[rightIndex - 1];
        const rightPoint = rendered[rightIndex];
        const ratio = (sampleX - leftPoint.x) / (rightPoint.x - leftPoint.x);
        return leftPoint.y + (rightPoint.y - leftPoint.y) * ratio;
      };
      const graphExtents = (fromX, toX) => {
        const samples = Array.from({ length: 25 }, (_, index) => graphYAt(fromX + ((toX - fromX) * index) / 24));
        return { top: Math.min(...samples), bottom: Math.max(...samples) };
      };

      root.replaceChildren();
      controls.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.cwmRange === key)));
      const rangeName = key === 'full' ? 'full history' : controls.find((button) => button.dataset.cwmRange === key).textContent.trim();
      svg.setAttribute('aria-label', `Editorial illustration of collectible whisky market conditions, ${rangeName}, from ${MONTHS[visible[0].date.getUTCMonth()]} ${visible[0].date.getUTCFullYear()} to ${MONTHS[latest.date.getUTCMonth()]} ${latest.date.getUTCFullYear()}`);

      const guides = svgEl('g', { class: 'cwm-market__guides', 'aria-hidden': 'true' });
      [0, 0.5, 1].forEach((ratio) => {
        const guideY = BOX.top + ratio * (BOX.bottom - BOX.top);
        guides.append(svgEl('line', { x1: BOX.left, y1: guideY, x2: BOX.right, y2: guideY }));
      });
      root.append(guides);

      const latestY = y(latest.level);
      const latestExtents = graphExtents(BOX.right - 64, BOX.right);
      const latestLabelY = Math.max(12, Math.min(latestY - 13, latestExtents.top - 10));
      root.append(svgEl('line', { x1: BOX.left, y1: latestY, x2: BOX.right, y2: latestY, class: 'cwm-market__latest-line', 'aria-hidden': 'true' }));
      root.append(svgEl('text', { x: BOX.right - 4, y: latestLabelY, class: 'cwm-market__latest-label', 'aria-hidden': 'true' }, 'LATEST'));

      const areaPoints = `${BOX.left},${BOX.bottom} ${coords} ${BOX.right},${BOX.bottom}`;
      root.append(svgEl('polygon', { points: areaPoints, fill: `url(#${gradient.id})`, 'aria-hidden': 'true' }));
      root.append(svgEl('polyline', { points: coords, class: 'cwm-market__line', 'aria-hidden': 'true' }));
      root.append(svgEl('line', { x1: BOX.left, y1: BOX.bottom, x2: BOX.right, y2: BOX.bottom, class: 'cwm-market__x-axis', 'aria-hidden': 'true' }));

      const years = svgEl('g', { class: 'cwm-market__years', 'aria-hidden': 'true' });
      axisDates(start, latest.date, key).forEach((date) => {
        const tickX = x(date);
        const compact = key === 'ytd' || key === '1y';
        const label = compact ? MONTHS[date.getUTCMonth()].toUpperCase() : String(date.getUTCFullYear());
        years.append(svgEl('line', { x1: tickX, y1: BOX.bottom, x2: tickX, y2: BOX.bottom + 5, class: 'cwm-market__tick' }));
        years.append(svgEl('text', { x: tickX, y: BOX.bottom + 27 }, label));
      });
      root.append(years);

      const annotations = svgEl('g', { class: 'cwm-market__annotations', 'aria-hidden': 'true' });
      document.annotations.forEach((annotation) => {
        const point = visible.find((item) => item.period === annotation.period);
        if (!point) return;
        const titleLines = annotation.title.length > 15 ? annotation.title.split(' ') : [annotation.title];
        const lines = [...titleLines, ...(annotation.subtitle ? [annotation.subtitle] : [])];
        const px = x(point.date), py = y(point.level);
        const labelHalfWidth = 48;
        const labelX = Math.max(BOX.left + labelHalfWidth, Math.min(px, BOX.right - labelHalfWidth));
        const lineStep = 15;
        const dotGap = 9;
        const localGraph = graphExtents(labelX - labelHalfWidth, labelX + labelHalfWidth);
        const labelBottom = Math.min(py - dotGap, localGraph.top - dotGap);
        const labelY = Math.max(11, labelBottom - 3 - (lines.length - 1) * lineStep);
        annotations.append(svgEl('line', { x1: px, y1: py, x2: px, y2: BOX.bottom, class: 'cwm-market__annotation-guide' }));
        annotations.append(svgEl('circle', { cx: px, cy: py, r: 4.5 }));
        addTextLines(annotations, labelX, labelY, lines, { 'text-anchor': 'middle' });
      });
      root.append(annotations);

      const points = svgEl('g', { class: 'cwm-market__points' });
      visible.forEach((point) => {
        const px = x(point.date), py = y(point.level);
        const group = svgEl('g', { class: 'cwm-market__point', transform: `translate(${px} ${py})`, tabindex: '0', role: 'group', 'aria-label': describePoint(point) });
        group.append(svgEl('line', { x1: 0, y1: 0, x2: 0, y2: BOX.bottom - py, class: 'cwm-market__point-guide', 'aria-hidden': 'true' }));
        group.append(svgEl('circle', { cx: 0, cy: 0, r: 16, class: 'cwm-market__point-hit', 'aria-hidden': 'true' }));
        group.append(svgEl('circle', { cx: 0, cy: 0, r: 3.5, class: 'cwm-market__point-marker', 'aria-hidden': 'true' }));
        group.append(popover(point, px, py));
        const activate = () => {
          points.querySelectorAll('.cwm-market__point.is-active').forEach((active) => active.classList.remove('is-active'));
          group.classList.add('is-active');
        };
        group.addEventListener('mouseenter', activate);
        group.addEventListener('focus', activate);
        group.addEventListener('mouseleave', () => group.classList.remove('is-active'));
        group.addEventListener('blur', () => group.classList.remove('is-active'));
        points.append(group);
      });
      root.append(points);
    }

    controls.forEach((button) => button.addEventListener('click', () => render(button.dataset.cwmRange)));
    render('full');
    return () => {
      render(currentKey);
      renderMilestones(milestoneContainer, state.active.milestones);
    };
  }

  if (typeof document !== 'undefined') {
    const runtimeKey = '__cwmMarketPerspectiveInitialized';
    const start = () => {
      if (window[runtimeKey]) return;
      window[runtimeKey] = true;
      const state = createMarketState();
      const rerender = Array.from(document.querySelectorAll('.cwm-market')).map((section) => init(section, state)).filter(Boolean);
      loadPublished(window.fetch.bind(window), state, () => rerender.forEach((render) => render()));
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { FALLBACK_DOCUMENT, FALLBACK_MILESTONES, validatePublicDocument, createMarketState, loadPublished, rangeStart, axisDates, describePoint, selectMilestones, renderMilestones };
  }
})();
